/**
 * i18next setup for the embed iframe SPA.
 *
 * Mirrors the RN client's pattern (`apps/WunderGuide/lang/i18n.ts`):
 *   - Lazy-load each locale via `i18next-resources-to-backend` so only
 *     `en` (the immediate fallback) ships in the initial bundle and other
 *     locales are fetched on demand.
 *   - Locale tag normalization happens in `resolveLocale.ts` *before*
 *     calling `changeLanguage` (handles aliases like no→nb and script
 *     subtag mapping like zh-CN→zh-Hans).
 *
 * Adding a new locale:
 *   1. Add the JSON file under `src/lang/`.
 *   2. Register it in `BUNDLED_LOADERS` in `lang/languageBackend.ts`.
 *   3. (Optional) Add region→script entries to `lang/localeMap.json`.
 */
import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import resourcesToBackend from 'i18next-resources-to-backend';

import {
  languageBackendLoader,
  SUPPORTED_LOCALES,
} from '../lang/languageBackend';

const FALLBACK = 'en';

i18n
  .use(initReactI18next)
  .use(
    resourcesToBackend((lng: string, ns: string) =>
      languageBackendLoader(lng, ns),
    ),
  )
  .init({
    // Initial lng is overridden the moment InstanceContext resolves the
    // partner's effective locale (see context/InstanceContext.tsx).
    lng: FALLBACK,
    fallbackLng: FALLBACK,
    supportedLngs: SUPPORTED_LOCALES.concat([FALLBACK]),
    nonExplicitSupportedLngs: true,
    // CRITICAL: do NOT use 'languageOnly' here. It strips script subtags
    // before asking the backend ('zh-Hans' → 'zh'), and our loaders are
    // keyed by full tags like 'zh-Hans' / 'zh-Hant'. With 'currentOnly',
    // i18next requests exactly the tag we passed to changeLanguage —
    // which is whatever resolveLocale chose. Region/script normalization
    // already happened in resolveLocale; we don't want i18next undoing it.
    load: 'currentOnly',
    interpolation: { escapeValue: false },
    react: { useSuspense: false },
    // Don't surface raw keys when a translation file is missing — fall
    // through to the fallbackLng entry instead.
    returnEmptyString: false,
    returnNull: false,
  });

export default i18n;
